// https://prazdevs.github.io/pinia-plugin-persistedstate/frameworks/nuxt-3.html
import { defineStore, acceptHMRUpdate } from 'pinia'
// import { updateStringList, updateStringObject, readString } from '#composables'
import { updateStringList, updateStringObject, readString } from '#shared/utils/stringFunc'
import { t } from '#shared/utils/locales'

export const useUserStore = defineStore(
  'user',
  () => {
    const toast = useToast()
    const { data } = getData()

    // State from API.
    const auth_id = ref<string>()

    // Personal info
    const first_name = ref<string>()
    const last_name = ref<string>()
    const phone = ref<string>()
    const email = ref<string>()
    const postal_code = ref<string>()
    const city = ref<string>()

    // User info
    const email_confirmed = ref<boolean>()
    const do_not_contact = ref<boolean>()
    const a_b_test = ref<string>()
    const opinion = ref<string>()
    const questions = ref<string>()
    const updated_at = ref<Date>()
    const memberships = ref<string>()
    const timestamp_pages = ref<string>()

    // Election
    const election_district = ref<string>()

    // Donor info
    const active_donor = ref<boolean>()
    const recent_donation = ref<boolean>()
    const has_nin = ref<boolean>()
    // const donate_block_start = ref<Date>()

    // Actions
    const done_sharing = ref<string>()
    const done_feedback = ref<string>()
    const done_material = ref<string>()

    const org = ref<string>()
    const org_email = ref<string>()
    const org_phone = ref<string>()
    const org_some = ref<string>()
    const org_postcard = ref<string>()
    const people = ref<string>()
    const people_email = ref<string>()
    const people_phone = ref<string>()
    const people_some = ref<string>()
    const people_postcard = ref<string>()

    // Local only
    // ----------------
    // UTMs
    const utm_entry = ref<string>()
    const utm_source = ref<string>()
    const utm_medium = ref<string>()
    const utm_campaign = ref<string>()
    const utm_content = ref<string>()
    const utm_term = ref<string>()

    // Getters.
    const full_name = computed(() => {
      if (first_name.value && last_name.value) return `${first_name.value} ${last_name.value}`.trim()
      return first_name.value ?? last_name.value ?? null
    })

    const steps_done = computed(() => {
      const { data: { site_id, default_to_organisations }, organisations } = getData()

      const done_sign: boolean = typeof auth_id.value === 'string' && auth_id.value.length > 0

      const done_org_email: boolean = typeof org_email.value === 'string' && organisations.every(org => org_email.value?.includes(org.id))
      const done_org_some: boolean = typeof org_some.value === 'string' && organisations.every(org => org_some.value?.includes(org.id))
      const done_org_phone: boolean = typeof org_phone.value === 'string' && organisations.every(org => org_phone.value?.includes(org.id))
      const done_contact: boolean = default_to_organisations ? done_org_email && done_org_some && done_org_phone : false

      const done_opinion: boolean = typeof opinion.value === 'string' && opinion.value.length > 0
      const done_questions: boolean = done_opinion

      const done_share: boolean = typeof done_sharing.value === 'string' && done_sharing.value?.includes(site_id)
      const done_donate: boolean = active_donor.value === true || recent_donation.value === true

      return {
        done_sign, // user cookie exists
        done_questions,
        done_opinion,
        done_donate,
        // Contact
        done_contact,
        done_org_email,
        done_org_some,
        done_org_phone,
        done_share,
      }
    })

    const next_page = computed(() => {
      const path = useRoute().path
      const { default_to_organisations } = getData().data

      function checkTime(check_path: string) {
        const current_time = Math.floor(new Date().getTime() / 1000) // seconds
        const time = readString(timestamp_pages.value, check_path)
        if (!time) return true
        return current_time - parseInt(time || '') > 60 * 60 // 1 hour
      }

      // const u: boolean = typeof auth_id.value === 'string' && auth_id.value.length > 0
      // const d: boolean = u && (active_donor.value === false || recent_donation.value === false)
      // const o: boolean = typeof opinion.value === 'string' && opinion.value.length > 0
      const contact_path: string = default_to_organisations ? '/org/contact' : '/people/contact'

      const { done_share, done_donate, done_contact, done_questions, done_sign } = steps_done.value

      if (path !== '/questions' && done_sign && !done_questions && checkTime('/questions')) return '/questions'
      else if (path !== contact_path && !done_contact && checkTime(contact_path)) return contact_path
      else if (path !== '/share' && !done_share && checkTime('/share')) return '/share'
      else if (path !== '/donate' && !done_donate && checkTime('/donate')) return '/donate'
      else if (done_sign) return '/thanks'
      else return '/'
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const refMap: { [key: string]: Ref<any> } = {
      auth_id,
      first_name,
      last_name,
      phone,
      email,
      postal_code,
      city,
      email_confirmed,
      a_b_test,
      opinion,
      questions,
      updated_at,
      done_sharing,
      done_feedback,
      done_material,
      org,
      org_email,
      org_phone,
      org_some,
      org_postcard,
      people,
      people_email,
      people_phone,
      people_some,
      people_postcard,
      memberships,
      election_district,
      utm_entry,
      active_donor,
      recent_donation,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_term,
      timestamp_pages,
      do_not_contact,
      has_nin,
      // donate_block_start,
    }

    type InputObject = Partial<Record<keyof typeof refMap, unknown>>

    function updateUser(input: InputObject) {
      useSentry(email.value)

      for (const ref_name in input) {
        if (!refMap[ref_name]) {
          console.error(`[User store] updateUser, Ref ${ref_name} does not exist`)
          continue
        }
        refMap[ref_name].value = input[ref_name]
      }
    }

    async function getUser(new_auth_id: string) {
      auth_id.value = new_auth_id
      try {
        const res = await useUser(auth_id.value).get() as InputObject
        debug(`[User store] User with auth_id ${auth_id.value} fetched`)
        updateUser(res)
      }
      catch (error) {
        toast.add({
          color: 'error',
          title: t('forms.error.title'),
          description: t('forms.error.description', { email: data.ngo.email }),
          duration: 3000,
        })
        console.error('[User store] Failed to get user:', error)
      }
    }

    async function saveAction(input: InputObject) {
      if (auth_id.value) {
        try {
          const { petition: { sf_campaign_id }, data: { site_id } } = getData()
          const res = await useUser(auth_id.value).action({
            timestamp_pages: timestamp_pages.value,
            utm_source: utm_source.value,
            utm_medium: utm_medium.value,
            utm_campaign: utm_campaign.value,
            utm_content: utm_content.value,
            utm_term: utm_term.value,
            website_id: site_id,
            sf_campaign_id,
            ...input,
          }) as InputObject
          updateUser(res)
          console.log('[User store] External user updated and action saved')
          toast.add({
            color: 'success',
            title: t('forms.success.title'),
            duration: 3000,
          })
        }
        catch (error) {
          console.error('[User store] Failed to update external user:', error)
          toast.add({
            color: 'error',
            title: t('forms.error.title'),
            description: t('forms.error.description', { email: data.ngo.email }),
            duration: 3000,
          })
          throw error
        }
      }
      else {
        updateUser(input)
        console.log('[User store] No auth_id, unable to update external user.')
      }
    }

    async function updateExternalUser(input: InputObject) {
      if (auth_id.value) {
        try {
          const res = await useUser(auth_id.value).update({
            timestamp_pages: timestamp_pages.value,
            ...input,
          }) as InputObject
          updateUser(res)
          console.log('[User store] External user updated')
          toast.add({
            color: 'success',
            title: t('forms.success.title'),
            duration: 3000,
          })
        }
        catch (error) {
          console.error('[User store] Failed to update external user:', error)
          toast.add({
            color: 'error',
            title: t('forms.error.title'),
            description: t('forms.error.description', { email: data.ngo.email }),
            duration: 3000,
          })
          throw error
        }
      }
      else {
        updateUser(input)
        console.log('[User store] No auth_id, unable to update external user.')
        return input
      }
    }

    function logPage(path: string) {
      if (!['/share', '/donate', '/questions', '/org/contact', '/people/contact'].includes(path)) return
      const current_time = String(Math.floor(new Date().getTime() / 1000))
      timestamp_pages.value = updateObject('timestamp_pages', path, current_time)
    }

    function currentUTM() {
      return utm_source.value ? `${utm_source.value},${utm_medium.value},${utm_campaign.value},${utm_content.value},${utm_term.value}` : 'utm\'s missing'
    }

    function resetUser() {
      for (const ref_name in refMap) {
        if (!refMap[ref_name]) {
          console.error(`[User store] resetUser, Ref ${ref_name} does not exist`)
          continue
        }

        refMap[ref_name].value = undefined
      }
    }

    function updateList(ref_name: string, new_value: string, add: boolean) {
      if (!refMap[ref_name]) {
        console.error(`[User store] updateList, Ref ${ref_name} does not exist`)
        return
      }
      return updateStringList(refMap[ref_name].value, new_value, add)
    }

    function updateObject(ref_name: string, field: string, new_value: string) {
      if (!refMap[ref_name]) {
        console.error(`[User store] updateObject, Ref ${ref_name} does not exist`)
        return
      }
      return updateStringObject(refMap[ref_name].value, field, new_value)
    }

    async function unsubscribe() {
      console.error('[User store] Unsubscribe not implemented')
    }

    return {
      // State
      auth_id,

      // Personal info
      first_name,
      last_name,
      phone,
      email,
      postal_code,
      city,

      // User info
      active_donor,
      recent_donation,
      opinion,
      email_confirmed,
      updated_at,
      a_b_test,
      has_nin,
      memberships,
      timestamp_pages,
      questions,
      do_not_contact,
      // donate_block_start,

      // Election
      election_district,

      // Actions
      done_sharing,
      done_feedback,
      done_material,

      org,
      org_email,
      org_phone,
      org_some,
      org_postcard,

      people,
      people_email,
      people_phone,
      people_some,
      people_postcard,

      // UTMs
      utm_source,
      utm_campaign,
      utm_medium,
      utm_content,
      utm_term,
      utm_entry,

      // Getters
      full_name,
      next_page,
      steps_done,

      // Actions
      updateUser,
      getUser,
      updateExternalUser,
      logPage,
      currentUTM,
      resetUser,
      updateList,
      updateObject,
      unsubscribe,
      saveAction,
    }
  },
  { persist: true },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
